/* eslint-disable react-hooks/exhaustive-deps */
// pages/ZonesPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getZonesByOrg,
  createZone,
  updateZone,
  deleteZone,
} from "../api/zoneApi";
import AccessControl from "../components/AccessControl";
import ZoneModal from "./ZoneModal";

// ── Constants ────────────────────────────────────────────────────────────────
const ZONE_TYPES = ["NATIONAL", "REGIONAL", "AREA", "TERRITORY"];

const TYPE_META = {
  NATIONAL: {
    color: "#6D28D9",
    bg: "#EDE9FE",
    border: "#C4B5FD",
    icon: "🌐",
    role: "NSM",
    next: "REGIONAL",
  },
  REGIONAL: {
    color: "#1D4ED8",
    bg: "#DBEAFE",
    border: "#93C5FD",
    icon: "🗺️",
    role: "RSM",
    next: "AREA",
  },
  AREA: {
    color: "#065F46",
    bg: "#D1FAE5",
    border: "#6EE7B7",
    icon: "📍",
    role: "ASM",
    next: "TERRITORY",
  },
  TERRITORY: {
    color: "#92400E",
    bg: "#FEF3C7",
    border: "#FCD34D",
    icon: "🏘️",
    role: "TSO",
    next: null,
  },
};

// ── ZoneCard ─────────────────────────────────────────────────────────────────
const ZoneCard = ({
  zone,
  zones,
  isSelected,
  isHighlighted,
  isDimmed,
  onSelect,
  onEdit,
  onDelete,
  userData,
}) => {
  const meta = TYPE_META[zone.type] || {};
  const parentZone = zones.find((x) => x.zoneId === zone.parentZoneId);

  return (
    <div
      onClick={() => onSelect(zone)}
      style={{
        background: isSelected ? meta.bg : isHighlighted ? meta.bg : "#fff",
        border: isSelected
          ? `2px solid ${meta.color}`
          : isHighlighted
            ? `1.5px solid ${meta.border}`
            : "1.5px solid #E5E7EB",
        borderRadius: 12,
        padding: "14px 16px",
        cursor: "pointer",
        opacity: isDimmed ? 0.35 : 1,
        transition: "all 0.18s ease",
        boxShadow: isSelected
          ? `0 0 0 3px ${meta.color}22, 0 4px 16px ${meta.color}18`
          : isHighlighted
            ? `0 2px 8px ${meta.color}15`
            : "0 1px 3px rgba(0,0,0,0.06)",
        transform: isSelected ? "translateY(-1px)" : "none",
        position: "relative",
      }}
    >
      {/* Selected indicator dot */}
      {isSelected && (
        <div
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: meta.color,
          }}
        />
      )}

      {/* Zone name + ID */}
      <div style={{ marginBottom: 8 }}>
        <p
          style={{
            margin: 0,
            fontWeight: 700,
            fontSize: 14,
            color: "#111",
            lineHeight: 1.3,
          }}
        >
          {zone.name}
        </p>
        <p
          style={{
            margin: "3px 0 0",
            fontSize: 11,
            color: "#9CA3AF",
            fontFamily: "monospace",
          }}
        >
          {zone.zoneId}
        </p>
      </div>

      {/* Parent link */}
      {parentZone && (
        <p
          style={{
            margin: "0 0 6px",
            fontSize: 11,
            color: meta.color,
            fontWeight: 500,
          }}
        >
          ↳ {parentZone.name}
        </p>
      )}

      {/* Description */}
      {zone.description && (
        <p
          style={{
            margin: "0 0 8px",
            fontSize: 12,
            color: "#6B7280",
            lineHeight: 1.4,
          }}
        >
          {zone.description}
        </p>
      )}

      {/* Location pill */}
      {(zone.location?.city || zone.location?.state) && (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "2px 8px",
            background: "#F3F4F6",
            borderRadius: 20,
            fontSize: 11,
            color: "#6B7280",
            marginBottom: 10,
          }}
        >
          📍{" "}
          {[zone.location.city, zone.location.state].filter(Boolean).join(", ")}
        </div>
      )}

      {/* Action buttons */}
      {["SUPERADMIN", "ADMIN"].includes(userData?.role) && (
        <div
          style={{ display: "flex", gap: 6, marginTop: 4 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => onEdit(zone)}
            style={{
              flex: 1,
              padding: "5px 0",
              background: "none",
              border: "1px solid #D1D5DB",
              borderRadius: 7,
              cursor: "pointer",
              fontSize: 12,
              color: "#374151",
              fontWeight: 500,
              transition: "all 0.12s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = meta.color;
              e.currentTarget.style.color = meta.color;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#D1D5DB";
              e.currentTarget.style.color = "#374151";
            }}
          >
            ✏️ Edit
          </button>
          <button
            onClick={() => onDelete(zone.zoneId)}
            style={{
              flex: 1,
              padding: "5px 0",
              background: "none",
              border: "1px solid #FCA5A5",
              borderRadius: 7,
              cursor: "pointer",
              fontSize: 12,
              color: "#DC2626",
              fontWeight: 500,
              transition: "all 0.12s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#FEF2F2";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "none";
            }}
          >
            🗑️ Delete
          </button>
        </div>
      )}
    </div>
  );
};

// ── ZoneColumn ────────────────────────────────────────────────────────────────
const ZoneColumn = ({
  type,
  zones,
  selectedZone,
  highlightedZoneIds,
  onSelect,
  onEdit,
  onDelete,
  onAdd,
  userData,
}) => {
  const meta = TYPE_META[type];
  const typeZones = zones.filter((z) => z.type === type);
  const hasAny = typeZones.length > 0;
  const someSelected = selectedZone !== null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minWidth: 240,
        flex: 1,
      }}
    >
      {/* Column Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 14px",
          background: meta.bg,
          borderRadius: "10px 10px 0 0",
          border: `1.5px solid ${meta.border}`,
          borderBottom: "none",
          marginBottom: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16 }}>{meta.icon}</span>
          <div>
            <p
              style={{
                margin: 0,
                fontWeight: 700,
                fontSize: 13,
                color: meta.color,
                letterSpacing: 0.3,
              }}
            >
              {type}
            </p>
            <p
              style={{
                margin: 0,
                fontSize: 10,
                color: meta.color,
                opacity: 0.7,
              }}
            >
              {meta.role} · {typeZones.length} zone
              {typeZones.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
        <button
          onClick={onAdd}
          title={`Add ${type} zone`}
          style={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            border: `1.5px solid ${meta.color}`,
            background: "#fff",
            color: meta.color,
            cursor: "pointer",
            fontSize: 16,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
            flexShrink: 0,
            transition: "all 0.12s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = meta.color;
            e.currentTarget.style.color = "#fff";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#fff";
            e.currentTarget.style.color = meta.color;
          }}
        >
          +
        </button>
      </div>

      {/* Cards scroll area */}
      <div
        style={{
          border: `1.5px solid ${meta.border}`,
          borderTop: "none",
          borderRadius: "0 0 10px 10px",
          padding: 12,
          display: "flex",
          flexDirection: "column",
          gap: 10,
          minHeight: 120,
          background: "#FAFAFA",
          flex: 1,
        }}
      >
        {!hasAny ? (
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "24px 12px",
              color: "#C9CDD4",
              textAlign: "center",
            }}
          >
            <span style={{ fontSize: 28, marginBottom: 6 }}>{meta.icon}</span>
            <p style={{ margin: 0, fontSize: 12 }}>
              No {type.toLowerCase()} zones
            </p>
            <button
              onClick={onAdd}
              style={{
                marginTop: 10,
                padding: "5px 14px",
                background: meta.bg,
                border: `1px solid ${meta.border}`,
                borderRadius: 20,
                color: meta.color,
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              + Add
            </button>
          </div>
        ) : (
          typeZones.map((z) => {
            const isSelected = selectedZone?.zoneId === z.zoneId;
            const isHighlighted = highlightedZoneIds.has(z.zoneId);
            const isDimmed = someSelected && !isSelected && !isHighlighted;
            return (
              <ZoneCard
                key={z.zoneId}
                zone={z}
                zones={zones}
                isSelected={isSelected}
                isHighlighted={isHighlighted}
                isDimmed={isDimmed}
                onSelect={onSelect}
                onEdit={onEdit}
                onDelete={onDelete}
                userData={userData}
              />
            );
          })
        )}
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ZonesPage() {
  const { orgId } = useParams();
  const navigate = useNavigate();

  const adminData = localStorage.getItem("admin");
  const userData = adminData ? JSON.parse(adminData) : null;
  console.log(userData,"<<< userDataaaaa")
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editZone, setEditZone] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null); // clicked zone for highlight

  const fetchZones = async () => {
    setLoading(true);
    try {
      const res = await getZonesByOrg(orgId);
      if (res.success !== false) setZones(res.zones || []);
      else toast.error(res.error || "Failed to load zones");
    } catch {
      toast.error("Failed to load zones");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchZones();
  }, [orgId]);

  // Deselect on outside area click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest("[data-zone-board]")) setSelectedZone(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Highlight logic ──────────────────────────────────────────────────────────
  // When a zone is selected, highlight its direct children
  const highlightedZoneIds = React.useMemo(() => {
    if (!selectedZone) return new Set();
    return new Set(
      zones
        .filter((z) => z.parentZoneId === selectedZone.zoneId)
        .map((z) => z.zoneId),
    );
  }, [selectedZone, zones]);

  const handleSelect = useCallback((zone) => {
    setSelectedZone((prev) => (prev?.zoneId === zone.zoneId ? null : zone));
  }, []);

  const openCreate = (defaultType) => {
    setEditZone(null);
    setShowModal(true);
    // Pass defaultType hint if needed — ZoneModal can pick it up
    sessionStorage.setItem("zone_default_type", defaultType || "REGIONAL");
  };
  const openEdit = (z) => {
    setEditZone(z);
    setShowModal(true);
  };

  const handleSave = async (formData) => {
    try {
      let res;
      if (editZone) {
        res = await updateZone(editZone.zoneId, formData);
        if (res.success !== false) toast.success("Zone updated");
        else {
          toast.error(res.error || "Update failed");
          return;
        }
      } else {
        res = await createZone(orgId, formData);
        if (res.success !== false) toast.success("Zone created");
        else {
          toast.error(res.error || "Create failed");
          return;
        }
      }
      setSelectedZone(null);
      fetchZones();
    } catch {
      toast.error("Operation failed");
    }
  };

  const handleDelete = async (zoneId) => {
    if (
      !window.confirm(
        "Delete this zone? Sub-zones and assigned staff will be unlinked.",
      )
    )
      return;
    try {
      const res = await deleteZone(zoneId);
      if (res.success !== false) {
        toast.success("Zone deleted");
        if (selectedZone?.zoneId === zoneId) setSelectedZone(null);
        fetchZones();
      } else toast.error(res.error || "Delete failed");
    } catch {
      toast.error("Delete failed");
    }
  };

  // Stats
  const stats = ZONE_TYPES.map((t) => ({
    type: t,
    count: zones.filter((z) => z.type === t).length,
    ...TYPE_META[t],
  }));
  const total = zones.length;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#F8F9FB",
        padding: "24px 20px 48px",
      }}
    >
      <AccessControl
        allowedRoles={["SUPERADMIN", "ADMIN","HR", "NSM", "RSM", "ASM", "TSO"]}
        redirectTo="/404"
      />

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            marginBottom: 28,
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div>
            <button
              onClick={() => navigate("/organizations")}
              style={{
                background: "none",
                border: "none",
                color: "#6D28D9",
                cursor: "pointer",
                fontSize: 12,
                padding: 0,
                marginBottom: 6,
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontWeight: 500,
              }}
            >
              ← Organizations
            </button>
            <h1
              style={{
                margin: 0,
                fontSize: 22,
                fontWeight: 800,
                color: "#111",
                letterSpacing: -0.3,
              }}
            >
              Zone Management
            </h1>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6B7280" }}>
              <span
                style={{
                  fontFamily: "monospace",
                  background: "#EDE9FE",
                  color: "#6D28D9",
                  padding: "1px 6px",
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                {orgId}
              </span>
              {"  "}· NSM → RSM → ASM → TSO hierarchy
            </p>
          </div>
          <button
            className="btn btn-success"
            onClick={() => openCreate()}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontWeight: 600,
              padding: "9px 18px",
            }}
          >
            + Add Zone
          </button>
        </div>

        {/* ── Stat Pills ───────────────────────────────────────────────────── */}
        {!loading && total > 0 && (
          <div
            style={{
              display: "flex",
              gap: 10,
              marginBottom: 24,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                padding: "6px 14px",
                background: "#fff",
                border: "1.5px solid #E5E7EB",
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600,
                color: "#374151",
              }}
            >
              All · {total}
            </div>
            {stats
              .filter((s) => s.count > 0)
              .map((s) => (
                <div
                  key={s.type}
                  style={{
                    padding: "6px 14px",
                    background: s.bg,
                    border: `1.5px solid ${s.border}`,
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 600,
                    color: s.color,
                  }}
                >
                  {s.icon} {s.type} · {s.count}
                </div>
              ))}
          </div>
        )}

        {/* ── Selection hint ────────────────────────────────────────────────── */}
        {!loading && total > 0 && (
          <div
            style={{
              marginBottom: 16,
              padding: "8px 14px",
              background: selectedZone
                ? `${TYPE_META[selectedZone.type]?.bg}`
                : "#fff",
              border: selectedZone
                ? `1.5px solid ${TYPE_META[selectedZone.type]?.border}`
                : "1.5px dashed #D1D5DB",
              borderRadius: 8,
              fontSize: 12,
              color: selectedZone
                ? TYPE_META[selectedZone.type]?.color
                : "#9CA3AF",
              fontWeight: selectedZone ? 600 : 400,
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {selectedZone ? (
              <>
                <span>{TYPE_META[selectedZone.type]?.icon}</span>
                <span>
                  <b>{selectedZone.name}</b> selected — child zones in{" "}
                  <b>{TYPE_META[selectedZone.type]?.next || "—"}</b> are
                  highlighted
                </span>
                <button
                  onClick={() => setSelectedZone(null)}
                  style={{
                    marginLeft: "auto",
                    background: "none",
                    border: "none",
                    color: "inherit",
                    cursor: "pointer",
                    fontSize: 15,
                    opacity: 0.6,
                  }}
                >
                  ✕
                </button>
              </>
            ) : (
              "Click any zone to highlight its children →"
            )}
          </div>
        )}

        {/* ── Zone Board ──────────────────────────────────────────────────── */}
        {loading ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 300,
              gap: 12,
              color: "#9CA3AF",
            }}
          >
            <div
              style={{
                width: 20,
                height: 20,
                border: "2.5px solid #E5E7EB",
                borderTopColor: "#6D28D9",
                borderRadius: "50%",
                animation: "spin 0.7s linear infinite",
              }}
            />
            Loading zones…
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : total === 0 ? (
          <div
            style={{ textAlign: "center", padding: "80px 0", color: "#9CA3AF" }}
          >
            <div style={{ fontSize: 52, marginBottom: 12 }}>🗺️</div>
            <p
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: "#374151",
                margin: "0 0 6px",
              }}
            >
              No zones yet
            </p>
            <p style={{ fontSize: 13, margin: "0 0 20px" }}>
              Build your hierarchy: National → Regional → Area → Territory
            </p>
            <button
              className="btn btn-success"
              onClick={() => openCreate("NATIONAL")}
            >
              + Create First Zone
            </button>
          </div>
        ) : (
          <div
            data-zone-board
            style={{
              display: "flex",
              gap: 16,
              alignItems: "flex-start",
              overflowX: "auto",
              paddingBottom: 8,
            }}
          >
            {ZONE_TYPES.map((type) => (
              <ZoneColumn
                key={type}
                type={type}
                zones={zones}
                selectedZone={selectedZone}
                highlightedZoneIds={highlightedZoneIds}
                onSelect={handleSelect}
                onEdit={openEdit}
                onDelete={handleDelete}
                onAdd={() => openCreate(type)}
                userData={userData}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Modal ──────────────────────────────────────────────────────────── */}
      <ZoneModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSave}
        zone={editZone}
        siblingZones={zones}
      />
    </div>
  );
}
